import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserGroup } from '@fortawesome/pro-light-svg-icons/faUserGroup';
import { TypedMemo } from '../../../common/types';
import { Panel, PanelBody, PanelHead } from '../../components/primitives/Panel';
import type { BrowseUsageData } from '../hooks/useBrowseUsage';
import { browseCreditMessages as messages } from '../browseMessages';
import { BrowseCreditsMeter } from './BrowseCreditsMeter';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './BrowseRail.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface BrowseFollowSlotsProps {
  usage: BrowseUsageData;
}

/** The follow slots of the workspace (design a1 rail, b7 "All slots are full"). */
const BrowseFollowSlotsComponent: React.FC<BrowseFollowSlotsProps> = ({ usage }) => {
  const intl = useIntl();
  const { artists, tracks } = usage.followSlots;
  const allFull = artists.used >= artists.limit && tracks.used >= tracks.limit;
  const rows = [
    { key: 'artists', label: intl.formatMessage(messages.slotsArtists), slots: artists },
    { key: 'tracks', label: intl.formatMessage(messages.slotsTracks), slots: tracks },
  ];

  return (
    <Panel as="section">
      <PanelHead
        title={intl.formatMessage(messages.slotsTitle)}
        icon={<FontAwesomeIcon icon={faUserGroup} />}
        right={intl.formatMessage(messages.slotsWorkspace)}
      />
      <PanelBody stack>
        {rows.map((row) => (
          <div key={row.key} className={styles.slot}>
            <div className={styles.slotLine}>
              <span>{row.label}</span>
              <span className={styles.slotFigure}>
                {intl.formatMessage(messages.slotsOf, { used: row.slots.used, limit: row.slots.limit })}
              </span>
            </div>
            {/* The bar shows the free slots, like the credits bar shows the credits left. */}
            <BrowseCreditsMeter left={row.slots.limit - row.slots.used} limit={row.slots.limit} thin />
          </div>
        ))}
        <p className={styles.rule}>{intl.formatMessage(allFull ? messages.slotsFullNote : messages.slotsNote)}</p>
      </PanelBody>
    </Panel>
  );
};

export const BrowseFollowSlots = TypedMemo(BrowseFollowSlotsComponent);
